const cleanUrl = (value) => String(value || '').replace(/[),.;:]+$/g, '');

const isHttpUrl = (value) => /^https?:\/\//i.test(value);

const parseInline = (input) => {
  const text = String(input || '');
  const tokens = [];
  const pattern = /(\*\*|__)(.+?)\1|\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)|(https?:\/\/[^\s)]+)/g;
  let last = 0;
  let match = pattern.exec(text);
  while (match) {
    if (match.index > last) tokens.push({ type: 'text', text: text.slice(last, match.index) });
    if (match[2]) {
      tokens.push({ type: 'bold', text: match[2] });
    } else if (match[4] && isHttpUrl(match[4])) {
      tokens.push({ type: 'link', text: match[3], url: match[4] });
    } else if (match[5]) {
      const url = cleanUrl(match[5]);
      const trailing = match[5].slice(url.length);
      if (isHttpUrl(url)) {
        tokens.push({ type: 'link', text: url, url });
        if (trailing) tokens.push({ type: 'text', text: trailing });
      } else tokens.push({ type: 'text', text: match[0] });
    } else {
      tokens.push({ type: 'text', text: match[0] });
    }
    last = match.index + match[0].length;
    match = pattern.exec(text);
  }
  if (last < text.length) tokens.push({ type: 'text', text: text.slice(last) });
  return tokens.length ? tokens : [{ type: 'text', text: '' }];
};

const splitRow = (line) =>
  String(line || '')
    .trim()
    .replace(/^\|/, '')
    .replace(/\|$/, '')
    .split('|')
    .map((cell) => cell.trim());

const isRow = (line) => /^\s*\|.*\|\s*$/.test(line || '');

const isDivider = (line) => {
  if (!line || !line.includes('-')) return false;
  const cells = splitRow(line);
  return cells.length > 1 && cells.every((cell) => /^:?-{3,}:?$/.test(cell));
};

const isBullet = (line) => /^\s*[-*]\s+/.test(line || '');
const isNumbered = (line) => /^\s*\d+\.\s+/.test(line || '');
const isHeading = (line) => /^\s{0,3}#{1,6}\s+/.test(line || '');

export const parseAnswer = (source) => {
  const lines = String(source || '')
    .replace(/\r\n/g, '\n')
    .split('\n');
  const blocks = [];
  let index = 0;

  while (index < lines.length) {
    const line = lines[index];
    if (!line.trim()) {
      index += 1;
      continue;
    }

    if (line.trim().startsWith('```')) {
      index += 1;
      const body = [];
      while (index < lines.length && !lines[index].trim().startsWith('```')) {
        body.push(lines[index]);
        index += 1;
      }
      if (index < lines.length) index += 1;
      const text = body.join('\n').trim();
      if (text) blocks.push({ type: 'paragraph', inlines: parseInline(text) });
      continue;
    }

    if (isRow(line) && isDivider(lines[index + 1])) {
      const headers = splitRow(line).map(parseInline);
      index += 2;
      const rows = [];
      while (index < lines.length && isRow(lines[index]) && !isDivider(lines[index])) {
        rows.push(splitRow(lines[index]).map(parseInline));
        index += 1;
      }
      blocks.push({ type: 'table', headers, rows });
      continue;
    }

    if (isBullet(line)) {
      const items = [];
      while (index < lines.length && isBullet(lines[index])) {
        items.push(parseInline(lines[index].replace(/^\s*[-*]\s+/, '')));
        index += 1;
      }
      blocks.push({ type: 'list', ordered: false, items });
      continue;
    }

    if (isNumbered(line)) {
      const items = [];
      while (index < lines.length && isNumbered(lines[index])) {
        items.push(parseInline(lines[index].replace(/^\s*\d+\.\s+/, '')));
        index += 1;
      }
      blocks.push({ type: 'list', ordered: true, items });
      continue;
    }

    const heading = line.match(/^\s{0,3}#{1,6}\s+(.*)$/);
    if (heading) {
      blocks.push({ type: 'paragraph', inlines: [{ type: 'bold', text: heading[1] }] });
      index += 1;
      continue;
    }

    const collected = [];
    while (
      index < lines.length &&
      lines[index].trim() &&
      !isBullet(lines[index]) &&
      !isNumbered(lines[index]) &&
      !isHeading(lines[index]) &&
      !lines[index].trim().startsWith('```') &&
      !(isRow(lines[index]) && isDivider(lines[index + 1]))
    ) {
      collected.push(lines[index]);
      index += 1;
    }
    const text = collected.join('\n').trim();
    if (text) blocks.push({ type: 'paragraph', inlines: parseInline(text) });
  }

  return blocks;
};
